import { animate, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  inject,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Dictionary } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, map, Observable } from 'rxjs';

import {
  BLAZE_CANVAS_SELECTOR,
  CanvasElement,
  CanvasElementId,
  CanvasNamespaceId,
  getRootElementId,
  PAGE_ROOT_ID_ATTR,
} from '@blaze-runtime/engine-domain';
import { type CanvasWidthLimit } from '@blaze-shared/api-contracts-domain';
import { ElementMetaData, getCanvasMaxWidth } from '@blaze-shared/elements-domain';
import { CssMediaService } from '@blaze-shared/theming-data-access';

import { State } from '../+state';
import { getOverlayIds } from '../+state/elements';
import {
  AddRelatedContext,
  ColumnAddHandleContext,
  ColumnDividerContext,
  ColumnResizeHandleContext,
  ElementWrapperContext,
  SelectionOverlayContext,
  SelectionToolbarContext,
  WorkflowErrorsContext,
} from '../node-renderer/node-renderer.template-contexts';
import { LayoutLoadingService } from '../services/layout-loading.service';
import { LayoutLockService } from '../services/layout-lock.service';
import { ViewportService } from '../services/viewport.service';

@UntilDestroy()
@Component({
  selector: BLAZE_CANVAS_SELECTOR,
  templateUrl: './canvas.component.html',
  styleUrls: ['./canvas.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fade', [
      transition(':enter', [style({ opacity: 0 }), animate(150, style({ opacity: 1 }))]),
      transition(':leave', [animate(150, style({ opacity: 0 }))]),
    ]),
  ],
  standalone: false,
  host: {
    '[attr.data-cy]': 'lockState()',
  },
})
export class CanvasComponent implements OnInit, OnDestroy {
  private readonly cssMediaService = inject(CssMediaService);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly layoutLoading = inject(LayoutLoadingService);
  private readonly layoutLock = inject(LayoutLockService);
  private readonly viewportService = inject(ViewportService);
  readonly store = inject<Store<State>>(Store);

  @Input() bypassLazyRendering = false;
  @Input() elements!: Dictionary<CanvasElement> | null;
  @Input() metadata!: Dictionary<ElementMetaData> | null;
  @Input() canvasWidthLimit: CanvasWidthLimit;
  @Input() selectionOverlayTpl: TemplateRef<SelectionOverlayContext> | null = null;
  @Input() columnAddHandleTpl: TemplateRef<ColumnAddHandleContext> | null = null;
  @Input() columnDividerTpl: TemplateRef<ColumnDividerContext> | null = null;
  @Input() columnResizeHandleTpl: TemplateRef<ColumnResizeHandleContext> | null = null;
  @Input() elementWrapperTpl: TemplateRef<ElementWrapperContext> | null = null;
  @Input() selectionToolbarTpl: TemplateRef<SelectionToolbarContext> | null = null;
  @Input() workflowErrorsTpl: TemplateRef<WorkflowErrorsContext> | null = null;
  @Input() addRelatedButtonTpl: TemplateRef<AddRelatedContext> | null = null;

  private readonly pageRootId$ = new BehaviorSubject<CanvasNamespaceId | undefined>(undefined);
  private originalDevicePixelRatio?: number;

  readonly rootElementIdArray$: Observable<CanvasElementId[]>;

  readonly lockState = toSignal(
    this.layoutLock.lock$.pipe(map((locked) => (locked ? 'canvas:loading' : 'canvas:ready'))),
  );

  @Input() set pageId(value: CanvasNamespaceId | null | undefined) {
    if (!value) {
      this.pageRootId$.next(undefined);
    } else {
      this.layoutLoading.markLayoutAsNeeded(value);
      const rootElementId = getRootElementId(value);
      this.pageRootId$.next(rootElementId);
    }
  }

  @HostBinding(`attr.${PAGE_ROOT_ID_ATTR}`) get pageRootId(): string | undefined {
    return this.pageRootId$.value;
  }
  @HostBinding(`style.max-width.px`) get maxWidth(): number | undefined {
    return getCanvasMaxWidth(this.canvasWidthLimit);
  }
  @HostBinding('class.mobile') get isMobile(): boolean {
    return this.viewportService.mode === 'mobile';
  }

  constructor() {
    const store = this.store;

    this.rootElementIdArray$ = combineLatest({
      pageId: this.pageRootId$,
      overlayIds: store.select(getOverlayIds),
    }).pipe(
      map(({ pageId, overlayIds }) => {
        const ids: CanvasElementId[] = [];
        if (pageId) {
          ids.push(pageId);
        }
        return ids.concat(overlayIds);
      }),
    );

    this.cssMediaService.isPrintMode$.pipe(untilDestroyed(this)).subscribe((isPrinting) => {
      if (isPrinting) {
        this.forceHighResolution();
      } else {
        this.restoreOriginalResolution();
      }
    });
  }

  ngOnInit(): void {
    this.viewportService.registerCanvasElement(this.elementRef.nativeElement);
  }

  ngOnDestroy(): void {
    this.viewportService.disposeCanvasElement(this.elementRef.nativeElement);
  }

  private forceHighResolution(): void {
    if (this.originalDevicePixelRatio === undefined) {
      // Store the original device pixel ratio so we can restore it later
      this.originalDevicePixelRatio = window.devicePixelRatio;
      // Override with high device pixel ratio to render the charts using canvas in high resolution
      window['devicePixelRatio'] = 4;
    }
  }

  private restoreOriginalResolution(): void {
    if (this.originalDevicePixelRatio !== undefined) {
      window['devicePixelRatio'] = this.originalDevicePixelRatio;
      this.originalDevicePixelRatio = undefined;
    }
  }
}
